import React, { useEffect, useState } from "react";
import API_URL from "../settings";
import {
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress
} from "@mui/material";
import { useAuth } from '../context/AuthContext';

/* ================================================= */
/* Dialog : Assign Volunteer */
/* ================================================= */
function AssignVolunteerDialog({
  open,
  onClose,
  patient,
  volunteers,
  onSaved
}) {
  const [volunteerId, setVolunteerId] = useState("");

  useEffect(() => {
    if (patient) {
      setVolunteerId(patient.volunteer_id ?? "");
    }
  }, [patient]);

  const handleSave = async () => {
    await fetch(`${API_URL}/patients/${patient.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        volunteer_id: volunteerId || null,
        updated_at: new Date().toISOString(),
      }),
    });

    onSaved();
    onClose();
  };

  if (!patient) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>กำหนดอาสาสมัครดูแล</DialogTitle>

      <DialogContent dividers>
        <Typography sx={{ mb: 2 }}>
          ผู้สูงอายุ: <strong>{patient.first_name} {patient.last_name}</strong>
        </Typography>

        <FormControl fullWidth>
          <InputLabel>อาสาสมัคร</InputLabel>
          <Select
            value={volunteerId}
            label="อาสาสมัคร"
            onChange={(e) => setVolunteerId(e.target.value)}
          >
            <MenuItem value="">
              <em>ยังไม่กำหนด</em>
            </MenuItem>
            {volunteers.map(v => (
              <MenuItem key={v.id} value={v.id}>
                {v.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>ยกเลิก</Button>
        <Button variant="contained" onClick={handleSave}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ================================================= */
/* Main Page */
/* ================================================= */
function PatientVolunteerAssign() {
  const [patients, setPatients] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const [selectedPatient, setSelectedPatient] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadData = async () => {
    setLoading(true);
    const [pRes, vRes] = await Promise.all([
      fetch(`${API_URL}/patients?hospital_id=${user.hospital_id}`),
      fetch(`${API_URL}/users?role=VOLUNTEER&hospital_id=${user.hospital_id}`)
    ]);

    setPatients(await pRes.json());
    setVolunteers(await vRes.json());
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const openDialog = (patient) => {
    setSelectedPatient(patient);
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold" }}>
        จัดการอาสาสมัครดูแลผู้สูงอายุ
      </Typography>

      <Paper sx={{ p: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ชื่อ-สกุล</TableCell>
                <TableCell>อายุ</TableCell>
                <TableCell>ADL ล่าสุด</TableCell>
                <TableCell>อาสาสมัครที่ดูแล</TableCell>
                <TableCell align="center">การจัดการ</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {patients.map(p => {
                const volunteer = volunteers.find(v => v.id === p.volunteer_id);

                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      {p.first_name} {p.last_name}
                    </TableCell>

                    <TableCell>{p.age || "-"}</TableCell>

                    <TableCell>
                      {p.adl_summary?.latest_group ? (
                        <Chip
                          size="small"
                          label={p.adl_summary.latest_group}
                          color={
                            p.adl_summary.latest_group === "ติดเตียง"
                              ? "error"
                              : p.adl_summary.latest_group === "ติดบ้าน"
                              ? "warning"
                              : "success"
                          }
                        />
                      ) : (
                        <Chip size="small" label="ยังไม่ประเมิน" />
                      )}
                    </TableCell>

                    <TableCell>
                      {volunteer ? (
                        <Chip
                          size="small"
                          color="primary"
                          label={`${volunteer.name} `}
                        />
                      ) : (
                        <Chip size="small" label="ยังไม่กำหนด" />
                      )}
                    </TableCell>

                    <TableCell align="center">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => openDialog(p)}
                      >
                        กำหนด / เปลี่ยน
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}

              {patients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    ไม่พบข้อมูลผู้สูงอายุ
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog */}
      <AssignVolunteerDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        patient={selectedPatient}
        volunteers={volunteers}
        onSaved={loadData}
      />
    </>
  );
}

export default PatientVolunteerAssign;
